import type { HTMLAttributes } from 'react';
import styled from 'styled-components';
import { GenericSvg } from '../../../../assets/svgs/GenericSVG';
import { colors } from '../../../../styles/colors';
import { DESKTOP_BREAKPOINT, DESKTOP_BREAKPOINT_NUMBER } from '../../../../styles/breakpoints';
import { maxOnboardingContentWidth, space } from '../../../../styles/onboardingUiTokens';
import { useInviteContext } from '../../hooks/useInviteContext';
import { useMatchMedia } from '../../hooks/useMatchMedia';
import { resolveOnboardingHeaderLogo } from '../../utils/resolveOnboardingHeaderLogo';
import { useOnboardingStore } from '../../store/onboardingStore';
import { Button } from '../actions/Button';
import { ProgressBar } from '../elements/ProgressBar';
import { useOnboardingLayout } from './OnboardingLayoutContext';

export type OnboardingHeaderProps = Omit<HTMLAttributes<HTMLElement>, 'children'> & {
  /** When true, the header is not rendered. */
  hidden?: boolean;
  /** When true, the progress bar under the navbar is omitted. */
  hideProgress?: boolean;
  /** Hides the back control — set on the first screen of the flow (D2). */
  hideBackButton?: boolean;
  /** Story / UI-Lab override; production resolves the logo from the invite context. */
  logoSrc?: string;
};

const LOGO_SLOT_PX = 130;

const mobileMax = `${DESKTOP_BREAKPOINT_NUMBER - 1}px`;

const HeaderRoot = styled.header`
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  background: ${colors.white};

  @media (max-width: ${mobileMax}) {
    position: sticky;
    top: 0;
    z-index: 10;
    padding: 0;
  }
`;

const Inner = styled.div`
  width: 100%;
  min-width: 0;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    max-width: ${maxOnboardingContentWidth};
    margin: 0 auto;
  }
`;

const HeaderStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
`;

const Navbar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: ${space.sm} 10px;
`;

const NavSlot = styled.div<{ $align: 'left' | 'right' }>`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: ${({ $align }) => ($align === 'left' ? 'flex-start' : 'flex-end')};
`;

const LogoSlot = styled.div`
  flex: 0 0 auto;
  width: ${LOGO_SLOT_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoImg = styled.img`
  display: block;
  max-width: 100%;
  max-height: 32px;
  object-fit: contain;
`;

/**
 * The ghost icon button's own horizontal padding (from `buttonSizeTokens.small.paddingX`) reads as
 * a much bigger inset than the bordered DE placeholder's tight visual edge on the other side, even
 * though both nav slots are the same width. Trimmed padding + a small negative margin nudge the
 * glyph toward the edge without shrinking the tap target below a reasonable touch size.
 */
const BackButton = styled(Button)`
  padding-left: 10px;
  padding-right: 10px;
  margin-left: -6px;
`;

/**
 * Back button, branded logo, and progress bar for the top of onboarding screens. Keeps loading the
 * invite preview (`useInviteContext`) so later screens and the logo resolver have it, even though
 * the header no longer renders the therapist card itself. The DE language placeholder was removed
 * (ERS-61 feedback) — no language switch ships until the flow is actually localized.
 */
export function OnboardingHeader({
  hidden = false,
  hideProgress = false,
  hideBackButton = false,
  logoSrc: logoSrcOverride,
  className,
  ...rest
}: OnboardingHeaderProps) {
  const isDesktop = useMatchMedia(`(min-width: ${DESKTOP_BREAKPOINT})`);
  const { currentStep, totalSteps, onBack } = useOnboardingLayout();

  const inviteContext = useOnboardingStore((s) => s.inviteContext);

  useInviteContext();

  if (hidden) {
    return null;
  }

  const resolvedLogo = resolveOnboardingHeaderLogo(inviteContext);
  const logoImgSrc = logoSrcOverride ?? (resolvedLogo.isPracticeBranding ? resolvedLogo.src : undefined);

  return (
    <HeaderRoot className={className} {...rest}>
      <Inner>
        <HeaderStack>
          <Navbar>
            <NavSlot $align="left">
              {!hideBackButton ? (
                <BackButton
                  type="button"
                  variant="ghost"
                  size="small"
                  icon="chevronleft"
                  aria-label="Zurück"
                  onClick={onBack}
                />
              ) : null}
            </NavSlot>
            <LogoSlot>
              {logoImgSrc ? (
                <LogoImg src={logoImgSrc} alt={resolvedLogo.alt} />
              ) : (
                <GenericSvg name="Logo" primaryColor={colors.dark} width={LOGO_SLOT_PX} height={21} />
              )}
            </LogoSlot>
            {/* Empty counterweight to the back-button slot so the logo stays optically centered. */}
            <NavSlot $align="right" />
          </Navbar>
          {!hideProgress ? (
            <ProgressBar
              currentStep={currentStep}
              totalSteps={totalSteps}
              thickness={isDesktop ? '6px' : '3px'}
              flush={!isDesktop}
            />
          ) : null}
        </HeaderStack>
      </Inner>
    </HeaderRoot>
  );
}
